import { useState } from "react";

import { Input } from "@buildpy/ui/input";
import { m2ToPyeong } from "@buildpy/core/modules/quote/area-unit";
import { areaOf } from "@buildpy/core/modules/quote/editor/reducer";
import { areaFromText } from "@buildpy/core/modules/quote/editor/sheet/cell-edit";

interface Props {
  value: number | null;
  invalid: boolean;
  disabled?: boolean;
  onChange: (value: number | null) => void;
}

const textOf = (value: number | null): string => (value === null ? "" : String(value));

// 새 면적이 오면 칸 글자를 바꿀지(null = 그대로) — 포커스 중 내가 보낸 값이 리듀서 규칙(areaOf)으로 다듬어져 돌아온 것이면 덮지 않는다.
export const areaDraftSync = (
  draft: string,
  value: number | null,
  focused: boolean,
  sent: number | null | undefined
): string | null => {
  const external = !focused || sent === undefined || value !== areaOf(sent);
  if (!external || areaOf(areaFromText(draft)) === value) return null;
  return textOf(value);
};

// 면적 칸(m², 시트·카드 공통 · #173) — 옆에 환산 평을 작게. 치는 중간값("3."·반올림 전 "34.567")을 지키려고 포커스 중엔 글자를 따로 들고, 떠날 때 맞춘다.
const AreaInput = ({ value, invalid, disabled = false, onChange }: Props) => {
  const [draft, setDraft] = useState(textOf(value));
  const [seen, setSeen] = useState(value);
  const [focused, setFocused] = useState(false);
  // 내가 마지막으로 올린 값 — 포커스 중이라도 이것과 다른 값이 오면(되살리기·되돌리기) 밖에서 바뀐 것이다.
  const [sent, setSent] = useState<number | null | undefined>(undefined);
  const pyeong = m2ToPyeong(value);
  if (seen !== value) {
    setSeen(value);
    const next = areaDraftSync(draft, value, focused, sent);
    if (next !== null) {
      setDraft(next);
      setSent(undefined);
    }
  }

  return (
    <label
      className="flex flex-col gap-y-1"
      title={invalid ? "수량식에 면적(평·m²)이 있습니다 — 면적을 넣어 주세요" : undefined}>
      <span className="text-xs text-muted-foreground">면적(m²)</span>
      <span className="flex items-center gap-x-1.5 text-sm">
        <span className="w-24">
          <Input
            aria-invalid={invalid}
            aria-label="면적(m²)"
            className="text-right tabular-nums"
            disabled={disabled}
            inputMode="decimal"
            inputSize="sm"
            name="area_m2"
            placeholder="없음"
            value={draft}
            onBlur={() => {
              setFocused(false);
              setDraft(textOf(value));
            }}
            onFocus={() => setFocused(true)}
            onChange={(e) => {
              const text = e.target.value;
              setDraft(text);
              const next = areaFromText(text);
              if (areaOf(next) === value) return;
              setSent(next);
              onChange(next);
            }}
          />
        </span>
        <span className="text-muted-foreground">m²</span>
        {pyeong !== null && <span className="text-xs text-muted-foreground tabular-nums">≈{pyeong}평</span>}
      </span>
    </label>
  );
};

export default AreaInput;
